// topbar — 53-я shell.
//
// Real desktop class names (.brand, .tb-mode-btn, .tb-share-btn, etc.) styled
// via @desktop-css/topbar.css. Three action groups + brand + tabs + download CTA.
// `data-mode=stage` tab → promo modal (mode stays 'rehearsal'). Share button →
// shareDialog (real feature). Export/Settings → promo modals. Feedback → external
// GitHub Issues. Hamburger toggles sidebarOpen for mobile drawer (CSS-driven).

import { setState, getState } from '../state.js';

export interface TopbarHandle {
  unmount: () => void;
}

const DOWNLOAD_URL = 'https://github.com/neodisa/band-way/releases/latest';
const FEEDBACK_URL = 'https://github.com/neodisa/band-way/issues/new';

export function mountTopbar(host: Element): TopbarHandle {
  host.textContent = '';

  // ── Brand ────────────────────────────────────────────────────────────────
  const brand = document.createElement('div');
  brand.className = 'brand';
  const brandMark = document.createElement('div');
  brandMark.className = 'brand-mark';
  brand.appendChild(brandMark);
  const brandTextCol = document.createElement('div');
  brandTextCol.style.display = 'flex';
  brandTextCol.style.flexDirection = 'column';
  brandTextCol.style.lineHeight = '1.1';
  const brandName = document.createElement('span');
  brandName.className = 'brand-name';
  brandName.textContent = 'Band Way';
  const brandSub = document.createElement('span');
  brandSub.className = 'brand-sub';
  brandSub.textContent = 'Studio · Rehearsal';
  brandTextCol.appendChild(brandName);
  brandTextCol.appendChild(brandSub);
  brand.appendChild(brandTextCol);
  host.appendChild(brand);

  // ── Hamburger (mobile only via CSS) ──────────────────────────────────────
  const burger = document.createElement('button');
  burger.className = 'rh-topbar-burger';
  burger.type = 'button';
  burger.setAttribute('aria-label', 'Open navigation');
  burger.textContent = '☰';
  burger.addEventListener('click', () => {
    setState({ sidebarOpen: !getState().sidebarOpen });
  });
  host.appendChild(burger);

  // ── Divider ──────────────────────────────────────────────────────────────
  const divider = document.createElement('div');
  divider.className = 'tb-divider';
  host.appendChild(divider);

  // ── Tabs (Rehearsal/Stage) ───────────────────────────────────────────────
  const seg = document.createElement('div');
  seg.className = 'seg';
  seg.setAttribute('role', 'tablist');
  seg.setAttribute('aria-label', 'Modes');
  const tabRehearsal = document.createElement('button');
  tabRehearsal.className = 'tb-mode-btn is-active';
  tabRehearsal.dataset.mode = 'rehearsal';
  tabRehearsal.type = 'button';
  tabRehearsal.textContent = 'Rehearsal';
  const tabStage = document.createElement('button');
  tabStage.className = 'tb-mode-btn stage';
  tabStage.dataset.mode = 'stage';
  tabStage.type = 'button';
  tabStage.textContent = '◈ Stage';
  tabStage.addEventListener('click', () => {
    setState({ promoModal: { open: true, feature: 'stage' } });
  });
  seg.appendChild(tabRehearsal);
  seg.appendChild(tabStage);
  host.appendChild(seg);

  // ── Spacer ───────────────────────────────────────────────────────────────
  const spacer = document.createElement('div');
  spacer.className = 'tb-spacer';
  host.appendChild(spacer);

  // ── Share / Export group ─────────────────────────────────────────────────
  const grpShare = document.createElement('div');
  grpShare.className = 'tb-group';
  const shareBtn = document.createElement('button');
  shareBtn.className = 'tb-share-btn';
  shareBtn.type = 'button';
  shareBtn.title = 'Share';
  shareBtn.textContent = 'Share';
  shareBtn.addEventListener('click', () => {
    setState({ shareDialog: { open: true } });
  });
  const exportBtn = document.createElement('button');
  exportBtn.className = 'tb-export-btn';
  exportBtn.type = 'button';
  exportBtn.title = 'Export stems and mix';
  exportBtn.textContent = 'Export';
  exportBtn.addEventListener('click', () => {
    setState({ promoModal: { open: true, feature: 'export' } });
  });
  grpShare.appendChild(shareBtn);
  grpShare.appendChild(exportBtn);
  host.appendChild(grpShare);

  // ── Feedback / Settings group ────────────────────────────────────────────
  const grpExtra = document.createElement('div');
  grpExtra.className = 'tb-group';
  const feedbackBtn = document.createElement('button');
  feedbackBtn.className = 'tb-feedback-btn';
  feedbackBtn.type = 'button';
  feedbackBtn.title = 'Report a problem';
  feedbackBtn.textContent = 'Report a problem';
  feedbackBtn.addEventListener('click', () => {
    window.open(FEEDBACK_URL, '_blank', 'noopener,noreferrer');
  });
  const settingsBtn = document.createElement('button');
  settingsBtn.className = 'icon-btn tb-settings-btn';
  settingsBtn.type = 'button';
  settingsBtn.title = 'Settings';
  settingsBtn.setAttribute('aria-label', 'Settings');
  settingsBtn.textContent = '⚙';
  settingsBtn.addEventListener('click', () => {
    setState({ promoModal: { open: true, feature: 'settings' } });
  });
  grpExtra.appendChild(feedbackBtn);
  grpExtra.appendChild(settingsBtn);
  host.appendChild(grpExtra);

  // ── Download CTA ─────────────────────────────────────────────────────────
  const cta = document.createElement('a');
  cta.className = 'rh-topbar-download';
  cta.href = DOWNLOAD_URL;
  cta.target = '_blank';
  cta.rel = 'noopener noreferrer';
  cta.textContent = '↓ Get desktop app';
  host.appendChild(cta);

  return {
    unmount: () => {
      host.textContent = '';
    },
  };
}
